/**
 * Authentication Service
 * Provides utilities for email validation, password validation, and authentication helpers
 */

/**
 * Email validation - matches common email patterns
 */
export const validateEmail = (email: string): { valid: boolean; error: string | null } => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  
  if (!email || email.trim() === '') {
    return { valid: false, error: 'Email is required' };
  }
  
  if (!emailRegex.test(email)) {
    return { valid: false, error: 'Please enter a valid email address' };
  }
  
  return { valid: true, error: null };
};

/**
 * Password validation with strength indicators
 */
export interface PasswordStrength {
  score: 0 | 1 | 2 | 3 | 4; // 0=weak, 1=fair, 2=good, 3=strong, 4=very strong
  feedback: string[];
  isValid: boolean;
}

export const validatePassword = (password: string): PasswordStrength => {
  const feedback: string[] = [];
  let score = 0;

  if (!password || password.length === 0) {
    return { score: 0, feedback: ['Password is required'], isValid: false };
  }

  // Length check
  if (password.length < 8) {
    feedback.push('At least 8 characters required');
  } else {
    score++;
  }

  if (password.length >= 12) {
    feedback.push('✓ 12+ characters');
    score++;
  }

  // Uppercase check
  if (!/[A-Z]/.test(password)) {
    feedback.push('Add an uppercase letter');
  } else {
    score++;
  }

  // Lowercase check
  if (!/[a-z]/.test(password)) {
    feedback.push('Add a lowercase letter');
  } else {
    score++;
  }

  // Number check
  if (!/[0-9]/.test(password)) {
    feedback.push('Add a number');
  } else {
    score++;
  }

  // Special character check
  if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
    feedback.push('Add a special character (!@#$%^&* etc)');
  } else {
    score++;
  }

  // Cap score at 4
  const finalScore = Math.min(score, 4) as 0 | 1 | 2 | 3 | 4;
  const isValid = finalScore >= 2; // At least "good" strength

  return {
    score: finalScore,
    feedback: feedback.length > 0 ? feedback : ['✓ Strong password'],
    isValid
  };
};

/**
 * Get password strength label
 */
export const getPasswordStrengthLabel = (score: number): string => {
  const labels = ['Very Weak', 'Weak', 'Fair', 'Good', 'Strong'];
  return labels[score] || 'Unknown';
};

/**
 * Get password strength color
 */
export const getPasswordStrengthColor = (score: number): string => {
  const colors = {
    0: 'bg-red-500',
    1: 'bg-red-400',
    2: 'bg-yellow-500',
    3: 'bg-blue-500',
    4: 'bg-green-500'
  };
  return colors[score as keyof typeof colors] || 'bg-gray-300';
};

/**
 * Validate full name
 */
export const validateFullName = (fullName: string): { valid: boolean; error: string | null } => {
  if (!fullName || fullName.trim() === '') {
    return { valid: false, error: 'Full name is required' };
  }

  if (fullName.trim().length < 2) {
    return { valid: false, error: 'Full name must be at least 2 characters' };
  }

  if (fullName.trim().length > 100) {
    return { valid: false, error: 'Full name must be less than 100 characters' };
  }

  return { valid: true, error: null };
};

/**
 * Validate password confirmation
 */
export const validatePasswordMatch = (password: string, confirmPassword: string): { valid: boolean; error: string | null } => {
  if (password !== confirmPassword) {
    return { valid: false, error: 'Passwords do not match' };
  }
  return { valid: true, error: null };
};

/**
 * Parse Supabase auth errors to user-friendly messages
 */
export const parseAuthError = (error: any): string => {
  const errorMessage = error?.message?.toLowerCase() || '';
  const errorCode = error?.code?.toLowerCase() || '';

  // Specific error handling
  if (errorMessage.includes('invalid login credentials') || errorCode === 'invalid_credentials') {
    return 'Invalid email or password. Please check and try again.';
  }

  if (errorMessage.includes('user already registered') || errorCode === 'user_already_exists') {
    return 'This email is already registered. Please sign in instead.';
  }

  if (errorMessage.includes('email not confirmed') || errorCode === 'email_not_confirmed') {
    return 'Please verify your email before signing in. Check your inbox for the verification link.';
  }

  if (errorMessage.includes('invalid email') || errorCode === 'invalid_email') {
    return 'Please enter a valid email address.';
  }

  if (errorMessage.includes('password') || errorMessage.includes('weak')) {
    return 'Password does not meet security requirements. Use 8+ characters with uppercase, lowercase, numbers, and symbols.';
  }

  if (errorMessage.includes('network') || errorCode === 'network_error') {
    return 'Network error. Please check your connection and try again.';
  }

  if (errorMessage.includes('timeout')) {
    return 'Request timed out. Please try again.';
  }

  if (errorMessage.includes('too many requests') || errorCode === 'over_request_rate_limit') {
    return 'Too many attempts. Please wait a few minutes before trying again.';
  }

  // Default error message - always return a string
  const message = error?.message || 'An authentication error occurred. Please try again.';
  return typeof message === 'string' ? message : 'An authentication error occurred. Please try again.';
};

/**
 * Check if user should be redirected after successful auth
 */
export const getPostAuthRedirectPath = (userMetadata?: any): string => {
  // Can be extended for different user types or roles
  return '/dashboard';
};

/**
 * Local storage helpers for auth state
 */
export const authStorage = {
  setRememberMe: (email: string) => {
    try {
      localStorage.setItem('nanolez_remember_email', email);
    } catch (e) {
      console.warn('Failed to save remember me preference:', e);
    }
  },

  getRememberMeEmail: (): string | null => {
    try {
      return localStorage.getItem('nanolez_remember_email');
    } catch (e) {
      console.warn('Failed to retrieve remember me preference:', e);
      return null;
    }
  },

  clearRememberMe: () => {
    try {
      localStorage.removeItem('nanolez_remember_email');
    } catch (e) {
      console.warn('Failed to clear remember me preference:', e);
    }
  }
};
/**
 * Check if an email is already registered in the system
 * Used during signup to prevent duplicate email registrations
 */
export const checkEmailExists = async (email: string): Promise<boolean> => {
  try {
    // Import supabase client
    const { supabase } = await import('../config/supabase');
    
    const { data, error } = await supabase
      .from('user_profiles')
      .select('email')
      .eq('email', email.toLowerCase().trim())
      .single();

    // If error is PGRST116, it means no record found (email doesn't exist)
    // If error is null and data exists, email already registered
    if (error?.code === 'PGRST116') {
      return false; // Email doesn't exist
    }

    if (error) {
      console.warn('Error checking email existence:', error);
      return false; // Assume not exists on error to not block signup
    }

    return !!data; // Return true if email exists
  } catch (error) {
    console.error('Error checking email uniqueness:', error);
    return false; // Default to not exists on error
  }
};